import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Actions, Card, Table, useNotify, Loader, Space, Icon, Button } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { ConfirmModal } from 'components/molecules';
import { useQueryParams, useQueryUpdate } from 'hooks/useQueryParams';
import { Plus } from 'resources/icons';
import { FormModal } from '../FormModal';
import { FormNomenclaturesReasons } from '../FormNomenclatures';
import { Results, Column, FilterType, Nomenclatures } from 'types';
import { useReasonsAPI } from 'api';
import { defaultFilters } from 'utils';
import { useRolePermission } from 'hooks/useRolePermission';
import { UserRoleCRM } from 'hooks/useFetcher/interfaces';

const formId = 'reasons';

export const NomenclaturesReasons: React.FC = () => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { updateQuery } = useQueryUpdate();
  const { getReasons, createReason, getReasonById, updateReason, deleteReason } = useReasonsAPI();

  const [isCreate, setIsCreate] = React.useState<boolean>(false);
  const [editItem, setEditItem] = React.useState();
  const [deleteItem, setDeleteItem] = React.useState();

  const hasEditPermission = useRolePermission([UserRoleCRM.admin]);

  const { data: reasons, isLoading } = useQuery<Results<Nomenclatures>>(
    ['reasons', { ...defaultFilters, ...params }],
    getReasons,
    {
      onError: () => {
        notify.error({ title: t('error.someThingIsWrong') });
      },
    },
  );

  const deleteReasonsMutation = useMutation(deleteReason, {
    onError: () => notify.error({ title: t('error.someThingIsWrong') }),
    onSuccess: () => {
      queryClient.invalidateQueries(['reasons']);
      setDeleteItem(undefined);
      notify.success({ title: t('success.successDataDelete') });
    },
  });

  const handleDelete = (): void => {
    if (deleteItem) {
      deleteReasonsMutation.mutate(deleteItem);
    }
  };

  const handleCancelDelete = (): void => {
    setDeleteItem(undefined);
  };

  const handleCloseModal = (): void => {
    setEditItem(undefined);
    setIsCreate(false);
  };

  const handleFilter = (type: FilterType, field: string): FilterType => {
    updateQuery({ ...params, ordering: type ? `${type === 'desc' ? '-' : ''}${field}` : type });
    return type;
  };

  const columns: Column<Nomenclatures>[] = React.useMemo(
    () => [
      {
        title: t('nomenclature.reasons'),
        onFilter: (type) => handleFilter(type, 'name'),
        render: ({ name }): React.ReactNode => name,
      },
      ...(hasEditPermission
        ? [
            {
              align: 'right',
              render: ({ id }) => (
                <Space justify="end">
                  <Actions placement="bottom">
                    <Actions.Item onClick={() => setEditItem(id)}>
                      {t('nomenclature.edit')}
                    </Actions.Item>
                    <Actions.Item onClick={() => setDeleteItem(id)}>
                      {t('nomenclature.delete')}
                    </Actions.Item>
                  </Actions>
                </Space>
              ),
            } as Column<Nomenclatures>,
          ]
        : []),
    ],
    [t, reasons, params],
  );

  return (
    <>
      <Space justify="space-between" className="mb-20">
        <h3 className="title">{`${t(`nomenclature.reasons`)}${
          reasons?.count ? ` (${reasons.count})` : ``
        }`}</h3>

        {hasEditPermission && (
          <Button
            type="primary"
            prefix={<Icon component={Plus} />}
            onClick={() => setIsCreate(true)}
          >
            {t('buttons.addNew')}
          </Button>
        )}
      </Space>

      <Card>
        <Loader loading={isLoading} size="regular">
          <Table
            emptyText={t('main.noData')}
            data={reasons?.results}
            columns={columns}
            className="no-border"
          />
        </Loader>
        <Card.Footer>
          <Pagination size={reasons?.total_pages || 0} />
        </Card.Footer>
      </Card>

      {!!deleteItem && <ConfirmModal onSuccess={handleDelete} onCancel={handleCancelDelete} />}

      {(editItem || isCreate) && (
        <FormModal formId={formId} onCancel={handleCloseModal} isCreate={isCreate}>
          <FormNomenclaturesReasons
            formId={formId}
            getItem={getReasonById}
            onFinish={isCreate ? createReason : updateReason}
            onClose={handleCloseModal}
            id={editItem}
          />
        </FormModal>
      )}
    </>
  );
};
